import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';

/**
 * Change/Set Password dialog used by both desktop avatar dropdown and mobile menu.
 *
 * Two modes:
 *  • `setMode=true`  — user has no local password yet (e.g. Google-only login),
 *    we skip the "current password" field and label the action "Set Password".
 *  • `setMode=false` — normal change flow, requires the current password.
 *
 * All input state lives in the parent Navbar; this is a pure presentational shell.
 */
export default function ChangePasswordDialog({
  open, onOpenChange, setMode = false,
  currentPassword, setCurrentPassword,
  newPassword, setNewPassword,
  confirmPassword, setConfirmPassword,
  passwordSaving, onSubmit,
}) {
  const title = setMode ? 'Set Password' : 'Change Password';
  const submitLabel = setMode ? 'Set Password' : 'Change Password';
  const busyLabel = setMode ? 'Setting…' : 'Changing…';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[400px] bg-white" data-testid="change-password-dialog">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {setMode && (
            <DialogDescription>
              You signed in with Google. Setting a password creates a fallback
              login method so you can still access your account if Google sign-in
              ever fails.
            </DialogDescription>
          )}
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4 py-2">
          {!setMode && (
            <div className="space-y-1.5">
              <Label htmlFor="nav-current-pw">Current Password</Label>
              <Input
                id="nav-current-pw" type="password" value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Enter current password" required
                data-testid="nav-current-password-input"
              />
            </div>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="nav-new-pw">New Password</Label>
            <Input
              id="nav-new-pw" type="password" value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Min 6 characters" required
              data-testid="nav-new-password-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="nav-confirm-pw">Confirm New Password</Label>
            <Input
              id="nav-confirm-pw" type="password" value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Repeat new password" required
              data-testid="nav-confirm-password-input"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button
              type="submit" className="bg-blue-500 hover:bg-blue-600"
              disabled={passwordSaving} data-testid="nav-change-password-submit"
            >
              {passwordSaving ? busyLabel : submitLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
